/**
 * Renders chatbot message text as a small, safe subset of markdown.
 *
 * The output goes straight into `v-html`, so this function is the only thing
 * standing between model/user text and the DOM. Two rules make that safe:
 *
 * 1. Everything is HTML-escaped *first* — including `&`, so entity-encoded
 *    payloads (`&#106;avascript:`) can't be re-interpreted downstream.
 * 2. Only the tags produced below can exist, and link hrefs are restricted to
 *    an allowlist of schemes. Escaping alone is not enough for links: escaping
 *    leaves `javascript:alert(1)` untouched, and dropping it into an href
 *    yields a working script URL.
 */

/** Schemes allowed in a link: http(s), mailto, or a site-relative path. */
const SAFE_HREF = /^(?:https?:|mailto:|\/(?!\/))/i

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function isSafeChatHref(url: string): boolean {
  return SAFE_HREF.test(url.trim())
}

export function formatChatMessage(content: string): string {
  return escapeHtml(content)
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Links — an unusable scheme leaves the markdown as visible plain text
    // rather than producing a live link.
    .replace(
      /\[(.*?)\]\((.*?)\)/g,
      (whole, text: string, url: string) =>
        isSafeChatHref(url)
          ? `<a href="${url}" target="_blank" rel="noopener">${text}</a>`
          : whole,
    )
    // Line breaks
    .replace(/\n/g, '<br>')
}
